import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Search, X, Plus, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, RefreshCw, Star, KeyRound, Gamepad2,
  Copy, Eye, EyeOff, ArrowDownCircle, ArrowUpCircle, Info,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../../components/ui/sheet';
import api from '../../../services/api';
import { NEON } from './playerHelpers';
import CashoutRulesPopup from '../CashoutRulesPopup';
import NoGameBalanceModal from './NoGameBalanceModal';
import SafeImage from '../../../components/SafeImage';
import { replacementAvailableAt } from '../../../utils/gameReplacement';
import { isBlockingGameOperation } from './gameOperationStatus';

const GAME_ACTION_TOAST_ID = 'game-action-feedback';
const CASHOUT_SUCCESS_MESSAGE = 'Your cashout is made.';
const quickGameToast = (type, message) => {
  const fn = type === 'error' ? toast.error : toast.success;
  fn(message, { id: GAME_ACTION_TOAST_ID, duration: 2000 });
};

const gameActionError = (error, fallback) => {
  const response = error?.response?.data || {};
  const detail = response.detail;
  if (detail && typeof detail === 'object') {
    return {
      code: String(detail.code || response.code || ''),
      message: String(detail.message || response.message || fallback),
    };
  }
  return {
    code: String(response.code || ''),
    message: String(detail || response.message || fallback),
  };
};

const confirmedSafeGameFailure = (detail) => (
  detail?.code === 'provider_confirmed_safe_failure'
  && detail?.final_status === 'failed'
  && detail?.money_movement_started === false
  && detail?.operation_locked !== true
);

const wholeRedeemAmount = (value) => Math.max(0, Math.floor(Number(value) || 0));
const trailingRedeemAmount = (value) => {
  const amount = Math.max(0, Number(value) || 0);
  return Math.max(0, Number((amount - wholeRedeemAmount(amount)).toFixed(2)));
};

const accountBalanceTimestamp = (account) => {
  const value = account?.last_balance_synced_at || account?.last_synced_at || account?.updated_at || account?.created_at;
  const time = Date.parse(value || '');
  return Number.isFinite(time) ? time : 0;
};

/* -------------------- MY GAMES SECTION -------------------- */
export default function MyGamesSection({ accounts, games, wallet, favorites, onToggleFav, onAdd, refresh, onBackgroundSync, onAccountPatch, searchQuery, setSearchQuery, focusAccountId }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lockedCardKey, setLockedCardKey] = useState(null);
  const [animDir, setAnimDir] = useState('left'); // left = next, right = prev
  const [animTick, setAnimTick] = useState(0);
  const [cashoutRuleDocs, setCashoutRuleDocs] = useState({ wallet: null, fp: null });
  const [gameOperations, setGameOperations] = useState([]);
  const refreshGameOperations = useCallback(async () => {
    try {
      const response = await api.get('/api/games/operations/active', {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setGameOperations(Array.isArray(response.data?.operations) ? response.data.operations : []);
    } catch {
      // Keep the last known state. A failed status poll cannot prove safety.
    }
  }, []);
  useEffect(() => {
    refreshGameOperations();
    const poll = setInterval(() => {
      if (!document.hidden) refreshGameOperations();
    }, 8000);
    const onFocus = () => refreshGameOperations();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(poll);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshGameOperations]);

  const loadCashoutRuleDocs = useCallback(async () => {
    try {
      const [walletRules, fpRules] = await Promise.all([
        api.get('/api/cashout-rules?source=wallet'),
        api.get('/api/cashout-rules?source=fp'),
      ]);
      setCashoutRuleDocs({
        wallet: walletRules.data || null,
        fp: fpRules.data || null,
      });
    } catch {
      setCashoutRuleDocs({ wallet: null, fp: null });
    }
  }, []);

  const myGames = useMemo(() => {
    const map = new Map();
    accounts.forEach(a => {
      const key = a.game_id || a.game_code;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    const list = Array.from(map.entries()).map(([key, accs]) => {
      const sortedAccounts = [...accs].sort((a, b) => {
        return (b.__creating ? 1 : 0) - (a.__creating ? 1 : 0);
      });
      const primaryAccount = sortedAccounts[0] || {};
      const isCustom = primaryAccount?.is_custom || primaryAccount?.provider_code === 'manual';
      const isCreating = primaryAccount?.__creating;
      let game;
      if (isCreating) {
        game = games.find(g => g.id === key || g.code === key) || {
          id: key,
          name: primaryAccount?.game_name || 'Your game',
          image: primaryAccount?.image_url,
          logo_url: primaryAccount?.image_url,
        };
      } else if (isCustom) {
        game = {
          id: key,
          name: primaryAccount?.game_name || 'Custom Game',
          image: primaryAccount?.image_url,
          logo_url: primaryAccount?.image_url,
          download_url: primaryAccount?.download_url,
          is_custom: true,
        };
      } else {
        game = games.find(g => g.id === key || g.code === key) || { id: key, name: primaryAccount?.game_name || 'Game' };
      }
      return {
        game,
        accounts: sortedAccounts,
        hasCreating: sortedAccounts.some(acc => acc.__creating),
      };
    });
    return list;
  }, [accounts, games]);

  const filteredGames = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return myGames;
    return myGames.filter(({ game, accounts: accs }) =>
      (game.name || '').toLowerCase().includes(q) ||
      accs.some(a => (a.username || a.provider_username || '').toLowerCase().includes(q))
    );
  }, [myGames, searchQuery]);

  // Flatten games × accounts into a single array of cards, one per viewport slot.
  const cardItems = useMemo(() => {
    const accountItems = filteredGames.flatMap(({ game, accounts: accs }) =>
      accs.map(acc => ({ key: acc.id, game, acc, isCreating: Boolean(acc.__creating) }))
    );
    return accountItems;
  }, [filteredGames]);

  // Keep the current card locked on screen while background balance
  // refreshes update account rows. Without this, a refresh could change the
  // array order and make the visible card appear to shuffle.
  useEffect(() => {
    if (!cardItems.length) {
      if (currentIdx !== 0) setCurrentIdx(0);
      if (lockedCardKey !== null) setLockedCardKey(null);
      return;
    }

    if (!lockedCardKey) {
      setLockedCardKey(cardItems[Math.min(currentIdx, cardItems.length - 1)]?.key || null);
      return;
    }

    const lockedIndex = cardItems.findIndex(item => item.key === lockedCardKey);
    if (lockedIndex >= 0) {
      if (lockedIndex !== currentIdx) setCurrentIdx(lockedIndex);
      return;
    }

    const nextIndex = Math.min(currentIdx, cardItems.length - 1);
    const nextKey = cardItems[nextIndex]?.key || null;
    if (nextIndex !== currentIdx) setCurrentIdx(nextIndex);
    if (nextKey !== lockedCardKey) setLockedCardKey(nextKey);
  }, [cardItems, currentIdx, lockedCardKey]);

  useEffect(() => {
    if (!focusAccountId) return;
    const nextIndex = cardItems.findIndex(item => item.key === focusAccountId);
    if (nextIndex >= 0) {
      setCurrentIdx(nextIndex);
      setLockedCardKey(cardItems[nextIndex]?.key || focusAccountId);
      setAnimDir('left');
      setAnimTick(t => t + 1);
    }
  }, [focusAccountId, cardItems]);

  useEffect(() => {
    let live = true;
    loadCashoutRuleDocs().catch(() => {
      if (live) setCashoutRuleDocs({ wallet: null, fp: null });
    });
    return () => { live = false; };
  }, [loadCashoutRuleDocs]);

  const total = cardItems.length;
  const goPrev = () => {
    if (total > 0) {
      setAnimDir('right');
      setAnimTick(t => t + 1);
      setCurrentIdx(i => {
        const nextIndex = (i - 1 + total) % total;
        setLockedCardKey(cardItems[nextIndex]?.key || null);
        return nextIndex;
      });
    }
  };
  const goNext = () => {
    if (total > 0) {
      setAnimDir('left');
      setAnimTick(t => t + 1);
      setCurrentIdx(i => {
        const nextIndex = (i + 1) % total;
        setLockedCardKey(cardItems[nextIndex]?.key || null);
        return nextIndex;
      });
    }
  };

  return (
    <div
      className="flex flex-col animate-fade-in-up"
      style={{ minHeight: 'calc(100dvh - 140px)', paddingTop: myGames.length > 0 ? 6 : 0 }}
      data-testid="my-games-section"
    >
      {myGames.length > 0 && (
        <div className="flex items-center gap-2 mb-3 px-1 pt-1 pb-1 overflow-visible">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#FFFFFF', zIndex: 2 }} />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder=""
              className="pl-11 pr-10 h-12 rounded-full"
              style={{
                background: 'rgba(8,14,10,0.7)',
                border: '1px solid color-mix(in oklab, var(--vl-neon, #004D61) 70%, transparent)',
                color: '#fff',
                boxShadow: '0 6px 18px color-mix(in oklab, var(--vl-neon, #004D61) 18%, transparent), inset 0 1px 0 rgba(255,255,255,0.05)',
              }}
              data-testid="my-games-search"
            />
            {!searchQuery && (
              <div
                className="absolute inset-0 left-11 right-10 flex items-center pointer-events-none overflow-hidden rounded-full"
                aria-hidden="true"
              >
                <span
                  className="truncate text-sm"
                  style={{ color: 'rgba(255,255,255,0.86)' }}
                >
                  Search your games
                </span>
              </div>
            )}
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full"
                style={{ color: '#FFFFFF' }}
                title="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={onAdd}
            className="shrink-0 flex items-center gap-1.5 px-4 h-12 rounded-full transition active:scale-95 hover:brightness-110"
            style={{
              background: 'linear-gradient(180deg, #FFD93D, #F4B400)',
              color: '#1A1100',
              border: '1px solid rgba(255,217,61,0.6)',
              boxShadow: '0 0 0 1px rgba(255,217,61,0.55), 0 0 16px rgba(255,217,61,0.45), 0 6px 16px rgba(244,180,0,0.4), inset 0 1px 0 rgba(255,255,255,0.6)'
            }}
            aria-label="Add Games"
            title="Add Games"
            data-testid="add-game-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={2.6} />
            <span className="display text-sm font-extrabold whitespace-nowrap">Add Games</span>
          </button>
        </div>
      )}

      {cardItems.length === 0 ? (
        <div
          className="my-games-empty-safe rounded-3xl p-6 sm:p-10 text-center flex-1 flex flex-col items-center justify-center min-h-[360px] sm:min-h-[440px]"
          style={{ background: 'rgba(8,14,10,0.6)', border: '0 solid transparent' }}
          data-testid="my-games-empty"
        >
          <button
            type="button"
            onClick={onAdd}
            className="mb-4 flex flex-col items-center gap-2 rounded-2xl px-5 py-3 transition active:scale-95 hover:brightness-110"
            style={{
              background: 'rgba(255,217,61,0.08)',
              border: '1px solid rgba(255,217,61,0.42)',
              boxShadow: '0 10px 24px rgba(0,0,0,0.28), 0 0 18px rgba(255,217,61,0.18), inset 0 1px 0 rgba(255,255,255,0.08)',
            }}
            data-testid="my-games-empty-add"
            aria-label="Open available games"
          >
            <span
              className="relative grid h-14 w-14 place-items-center rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, #FFD93D, #F4B400)',
                color: '#1A1100',
                boxShadow: '0 8px 18px rgba(244,180,0,0.34), inset 0 1px 0 rgba(255,255,255,0.45)',
              }}
            >
              <Gamepad2 className="h-7 w-7" strokeWidth={2.6} />
              <span className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-white text-[#1A1100] shadow-[0_0_12px_rgba(255,255,255,0.45)]">
                <Plus className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
            </span>
            <span className="display text-sm font-extrabold text-[#FFD93D]">Add Games</span>
          </button>
          <p className="display text-lg sm:text-xl font-bold max-w-[280px]" style={{ color: '#fff' }}>You haven&apos;t added any games yet</p>
          <p className="text-sm mt-1 mb-5 max-w-[310px]" style={{ color: '#D7EFF3' }}>Tap <span className="font-bold" style={{ color: '#FFD93D' }}>Add Games</span> and pick from our library to get started.</p>
        </div>
      ) : total === 0 ? (
        <div className="rounded-2xl p-8 text-center flex-1 flex flex-col items-center justify-center" style={{ background: 'rgba(8,14,10,0.6)', border: '1px dashed rgba(0,107,133,0.4)' }}>
          <p className="display text-lg font-bold" style={{ color: '#fff' }}>No games match &quot;{searchQuery}&quot;</p>
          <p className="text-sm mt-1" style={{ color: '#D7EFF3' }}>Try a different name or username.</p>
        </div>
      ) : (
        <div
          className="relative min-h-0 flex-1 flex flex-col"
          data-testid="my-games-viewer"
        >
          <div className="min-w-0 flex items-start justify-center overflow-visible pt-1 pb-4">
            {(() => {
              const item = cardItems[currentIdx] || cardItems[0];
              return (
                <div
                  key={`${currentIdx}-${animTick}`}
                  className={`w-full max-w-2xl mx-auto card-anim-${animDir}`}
                  data-testid={`my-games-card-${currentIdx}`}
                >
                  <GameAccountCard
                    key={item.acc.id}
                    game={item.game}
                    acc={item.acc}
                    wallet={wallet}
                    cashoutRuleDocs={cashoutRuleDocs}
                    refreshCashoutRules={loadCashoutRuleDocs}
                    isFav={favorites.has(item.game.id)}
                    onToggleFav={() => onToggleFav(item.game.id)}
                    refresh={refresh}
                    onBackgroundSync={onBackgroundSync}
                    serverOperation={gameOperations.find(operation => (
                      operation.game_id === item.game.id
                      && (!operation.account_id || operation.account_id === item.acc.id)
                    ))}
                    onGameOperation={operation => {
                      if (!operation) return;
                      setGameOperations(previous => [operation, ...previous.filter(item => item.game_id !== operation.game_id)]);
                    }}
                    onAccountPatch={onAccountPatch}
                  />
                </div>
              );
            })()}
          </div>

          {total > 1 && (
            <div
              className="shrink-0 pt-3 pb-2 flex flex-col items-center gap-1.5"
              data-testid="my-games-swipe-controls"
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={goPrev}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition active:scale-90 hover:scale-105"
                  style={{
                    background: 'linear-gradient(180deg, color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 24%, transparent), color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 8%, transparent))',
                    border: '1.5px solid color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 58%, transparent)',
                    boxShadow: '0 6px 18px color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 30%, transparent), inset 0 1px 0 rgba(255,255,255,0.15)',
                    color: 'var(--vl-profile-accent, var(--vl-neon, #004D61))',
                  }}
                  aria-label="Previous game"
                  data-testid="game-prev-btn"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={2.6} />
                </button>
                <div
                  className="display text-xs font-extrabold tracking-wider px-2.5 py-1.5 rounded-md"
                  style={{ color: '#FFD93D', background: 'rgba(8,14,10,0.75)', border: '1px solid rgba(255,217,61,0.45)' }}
                  data-testid="game-position-indicator"
                >
                  {currentIdx + 1}/{total}
                </div>
                <button
                  onClick={goNext}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition active:scale-90 hover:scale-105"
                  style={{
                    background: 'linear-gradient(180deg, color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 24%, transparent), color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 8%, transparent))',
                    border: '1.5px solid color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 58%, transparent)',
                    boxShadow: '0 6px 18px color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 30%, transparent), inset 0 1px 0 rgba(255,255,255,0.15)',
                    color: 'var(--vl-profile-accent, var(--vl-neon, #004D61))',
                  }}
                  aria-label="Next game"
                  data-testid="game-next-btn"
                >
                  <ChevronRight className="w-5 h-5" strokeWidth={2.6} />
                </button>
              </div>
              <p
                className="text-[11px] sm:text-xs font-semibold tracking-wide"
                style={{ color: 'rgba(255,255,255,0.62)' }}
                data-testid="swipe-helper-text"
              >
                Tap the left / right arrows to see your added games
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const limitNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const normalizeCashoutTable = (rows) => (
  Array.isArray(rows)
    ? rows
        .map((row) => ({
          min: limitNumber(row?.min),
          max: limitNumber(row?.max),
          mode: String(row?.mode || '').toLowerCase() === 'multiplier' ? 'multiplier' : 'fixed',
          value: limitNumber(row?.value),
          label: row?.label || '',
        }))
        .filter((row) => row.value > 0)
    : []
);

const sourceLimits = (docs, source) => {
  const doc = String(source || '').toLowerCase() === 'fp' ? docs?.fp : docs?.wallet;
  return {
    minLoad: limitNumber(doc?.min_game_load),
    maxLoad: limitNumber(doc?.max_game_load),
    minWithdraw: limitNumber(doc?.min_game_withdraw),
    maxWithdraw: limitNumber(doc?.max_game_withdraw),
    minRedeemMultiplier: limitNumber(doc?.min_redeem_multiplier),
    maxRedeemMultiplier: limitNumber(doc?.max_redeem_multiplier),
    minCashoutMode: doc?.min_cashout_mode || 'multiplier',
    maxCashoutMode: doc?.max_cashout_mode || 'multiplier',
    minCashoutTable: normalizeCashoutTable(doc?.min_cashout_table),
    maxCashoutTable: normalizeCashoutTable(doc?.max_cashout_table),
    highDepositMin: limitNumber(doc?.high_deposit_min),
    lowDepositMinMultiplier: limitNumber(doc?.low_deposit_min_multiplier),
    highDepositMinMultiplier: limitNumber(doc?.high_deposit_min_multiplier),
    lowDepositMaxMultiplier: limitNumber(doc?.low_deposit_max_multiplier),
    highDepositMaxMultiplier: limitNumber(doc?.high_deposit_max_multiplier),
  };
};

const formatLimitRange = (min, max) => {
  const parts = [];
  if (min > 0) parts.push(`min $${min.toFixed(2)}`);
  if (max > 0) parts.push(`max $${max.toFixed(2)}`);
  return parts.length ? parts.join(' · ') : 'No fixed limit';
};

const formatFpAmountRange = (min, max) => {
  const parts = [];
  if (min > 0) parts.push(`min ${min.toFixed(2)} FP`);
  if (max > 0) parts.push(`max ${max.toFixed(2)} FP`);
  return parts.length ? parts.join(' · ') : 'No fixed limit';
};

const isThresholdUnlimited = (limits) => (
  String(limits?.maxCashoutMode || '').toLowerCase() === 'threshold_unlimited'
  && limitNumber(limits?.highDepositMin) > 0
);

const cashoutTableFor = (limits, type = 'max') => {
  const table = type === 'min' ? limits?.minCashoutTable : limits?.maxCashoutTable;
  return Array.isArray(table) ? table : [];
};

const hasCashoutTable = (limits, type = 'max') => cashoutTableFor(limits, type).length > 0;

const isTableCashout = (limits) => (
  String(limits?.maxCashoutMode || '').toLowerCase() === 'table'
  && hasCashoutTable(limits, 'max')
);

const minMultiplierForLoad = (limits, lastLoad) => {
  const threshold = limitNumber(limits?.highDepositMin);
  const lowMin = limitNumber(limits?.lowDepositMinMultiplier);
  const highMin = limitNumber(limits?.highDepositMinMultiplier);
  const defaultMin = limitNumber(limits?.minRedeemMultiplier);
  if (String(limits?.minCashoutMode || '').toLowerCase() === 'threshold_multiplier' && lastLoad > 0 && threshold > 0) {
    return lastLoad >= threshold
      ? (highMin || defaultMin)
      : (lowMin || defaultMin);
  }
  return defaultMin || highMin || lowMin;
};

const cashoutTableRowForLoad = (limits, lastLoad, type = 'max') => {
  const table = cashoutTableFor(limits, type);
  if (!table.length || lastLoad <= 0) return null;
  const rows = [...table].sort((a, b) => limitNumber(a.min) - limitNumber(b.min));
  let selected = null;
  rows.forEach((row) => {
    const rowMin = limitNumber(row.min);
    const rowMax = limitNumber(row.max);
    if (lastLoad >= rowMin && (rowMax <= 0 || lastLoad <= rowMax)) {
      selected = row;
    }
  });
  return selected;
};

const cashoutAmountForLoad = (limits, lastLoad, type = 'max') => {
  const row = cashoutTableRowForLoad(limits, lastLoad, type);
  if (!row) return 0;
  const value = limitNumber(row.value);
  return row.mode === 'multiplier' ? lastLoad * value : value;
};

const minCashoutAmountForLoad = (limits, lastLoad) => cashoutAmountForLoad(limits, lastLoad, 'min');

const maxCashoutAmountForLoad = (limits, lastLoad) => {
  return cashoutAmountForLoad(limits, lastLoad, 'max');
};

const formatCashoutTableRow = (row) => {
  const min = limitNumber(row.min);
  const max = limitNumber(row.max);
  const range = max > 0 ? `$${min.toFixed(0)}-$${max.toFixed(0)}` : `$${min.toFixed(0)}+`;
  const value = row.mode === 'multiplier' ? `${limitNumber(row.value)}x` : money(row.value);
  return `${range}: ${value}`;
};

const formatCashoutTable = (limits, type = 'max') => {
  const table = cashoutTableFor(limits, type);
  if (!table.length) return '';
  return table.map(formatCashoutTableRow).join(' · ');
};

const maxMultiplierForLoad = (limits, lastLoad) => {
  if (isTableCashout(limits)) {
    return 0;
  }
  if (isThresholdUnlimited(limits) && lastLoad > 0) {
    return lastLoad >= limitNumber(limits?.highDepositMin)
      ? 0
      : (limitNumber(limits?.lowDepositMaxMultiplier) || limitNumber(limits?.maxRedeemMultiplier));
  }
  return limitNumber(limits?.maxRedeemMultiplier);
};

const formatMultiplierRange = (limits) => {
  if (isTableCashout(limits)) {
    const minTable = formatCashoutTable(limits, 'min');
    const maxTable = formatCashoutTable(limits, 'max');
    return minTable
      ? `Min table: ${minTable} · Max table: ${maxTable}`
      : `No minimum · Max table: ${maxTable}`;
  }
  if (isThresholdUnlimited(limits)) {
    const threshold = limitNumber(limits?.highDepositMin);
    const lowMax = limitNumber(limits?.lowDepositMaxMultiplier) || limitNumber(limits?.maxRedeemMultiplier);
    const lowMin = limitNumber(limits?.lowDepositMinMultiplier);
    const highMin = limitNumber(limits?.highDepositMinMultiplier) || limitNumber(limits?.minRedeemMultiplier);
    const parts = [];
    if (lowMin > 0) parts.push(`below $${threshold.toFixed(2)} min ${lowMin}x`);
    if (highMin > 0) parts.push(`$${threshold.toFixed(2)}+ min ${highMin}x`);
    if (lowMax > 0) parts.push(`below $${threshold.toFixed(2)} max ${lowMax}x`);
    parts.push(`$${threshold.toFixed(2)}+ max unlimited`);
    return parts.join(' · ');
  }
  const min = limitNumber(limits?.minRedeemMultiplier);
  const max = limitNumber(limits?.maxRedeemMultiplier);
  const parts = [];
  if (min > 0) parts.push(`min ${min}x`);
  if (max > 0) parts.push(`max ${max}x`);
  return parts.length ? parts.join(' · ') : 'No multiplier limit';
};

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const buildWithdrawGuide = ({ source, lastLoad, limits, currentBalance = null }) => {
  const hasCurrentBalance = currentBalance !== null && currentBalance !== undefined;
  const currentAmount = Number(currentBalance || 0);
  const isFp = source === 'fp';
  if (isFp) {
    const minAmount = limitNumber(limits?.minWithdraw);
    const maxAmount = limitNumber(limits?.maxWithdraw);
    const minLabel = minAmount > 0 ? `${minAmount.toFixed(2)} FP` : 'No minimum';
    const maxLabel = maxAmount > 0 ? `${maxAmount.toFixed(2)} FP` : 'No fixed cap';
    let currentNote = 'Your current game balance will return to FP balance after withdrawal.';
    if (hasCurrentBalance && maxAmount > 0 && currentAmount > maxAmount) {
      currentNote = `Only ${maxLabel} can return to FP. The extra will be terminated.`;
    } else if (hasCurrentBalance && minAmount > 0 && currentAmount < minAmount) {
      currentNote = `Current balance is below ${minLabel}, so withdrawal can be blocked by FP rules.`;
    }
    return {
      sourceLabel: 'Freeplay',
      minLabel,
      maxLabel,
      body: `Freeplay withdrawals use fixed FP limits: min ${minLabel}, max ${maxLabel}.`,
      footer: currentNote,
    };
  }

  if (isTableCashout(limits)) {
    const minAmount = minCashoutAmountForLoad(limits, lastLoad);
    const maxAmount = maxCashoutAmountForLoad(limits, lastLoad);
    const hasActiveLoad = lastLoad > 0;
    const minLabel = hasActiveLoad
      ? (minAmount > 0 ? money(minAmount) : 'Not configured')
      : 'Based on active load';
    const maxLabel = hasActiveLoad
      ? (maxAmount > 0 ? money(maxAmount) : 'Not configured')
      : 'Based on active load';
    const body = lastLoad > 0
      ? `Based on your active load total of ${money(lastLoad)}, min is ${minLabel} and max is ${maxLabel}.`
      : `Cashout minimum and maximum follow the active load table: ${formatMultiplierRange(limits)}.`;
    let currentNote = !hasActiveLoad
      ? 'Load a game to calculate your exact minimum and maximum cashout.'
      : (maxAmount > 0
        ? `Anything above ${maxLabel} will be terminated.`
        : 'Cashout limits are not configured for this active load.');
    if (hasCurrentBalance && minAmount > 0 && currentAmount < minAmount) {
      currentNote = `Current game balance is below ${minLabel}, so it does not meet the withdrawal minimum.`;
    } else if (hasCurrentBalance && maxAmount > 0 && currentAmount > maxAmount) {
      currentNote = `Only ${maxLabel} can be cashed out. The extra will be terminated.`;
    }
    return {
      sourceLabel: 'Main Balance',
      minLabel,
      maxLabel,
      body,
      footer: currentNote,
    };
  }

  const thresholdUnlimited = isThresholdUnlimited(limits);
  const minMul = minMultiplierForLoad(limits, lastLoad);
  const maxMul = maxMultiplierForLoad(limits, lastLoad);
  const minAmount = lastLoad > 0 && minMul > 0 ? lastLoad * minMul : 0;
  const maxAmount = lastLoad > 0 && maxMul > 0 ? lastLoad * maxMul : 0;
  const minLabel = minAmount > 0 ? money(minAmount) : 'No minimum';
  const maxLabel = thresholdUnlimited && lastLoad >= limitNumber(limits?.highDepositMin)
    ? 'Unlimited'
    : (maxAmount > 0 ? money(maxAmount) : 'No fixed cap');
  let body = `Your active Main Balance load total sets the live withdrawal guide. Min ${minLabel}, max ${maxLabel}.`;
  if (lastLoad > 0) {
    body = `Based on your active load total of ${money(lastLoad)}, min is ${minLabel} and max is ${maxLabel}.`;
  } else if (thresholdUnlimited) {
    const threshold = limitNumber(limits?.highDepositMin);
    const lowMax = limitNumber(limits?.lowDepositMaxMultiplier) || limitNumber(limits?.maxRedeemMultiplier);
    body = `Below $${threshold.toFixed(2)} loads can cash out up to ${lowMax}x. $${threshold.toFixed(2)}+ loads show max unlimited.`;
  }
  let currentNote = maxAmount > 0
    ? `Anything above ${maxLabel} will be terminated.`
    : (thresholdUnlimited && lastLoad >= limitNumber(limits?.highDepositMin)
      ? 'This active load has no maximum cashout cap.'
      : 'No fixed max cap is configured right now.');
  if (hasCurrentBalance && minAmount > 0 && currentAmount < minAmount) {
    currentNote = `Current game balance is below ${minLabel}, so it does not meet the withdrawal minimum.`;
  }
  return {
    sourceLabel: 'Main Balance',
    minLabel,
    maxLabel,
    body,
    footer: currentNote,
  };
};

const getWithdrawLock = ({ source, lastLoad, limits, currentBalance = 0 }) => {
  const balance = Number(currentBalance || 0);
  if (balance <= 0) {
    return { locked: true, minAmount: 0, message: 'No game balance to withdraw yet.' };
  }
  if (source === 'fp') {
    const minAmount = limitNumber(limits?.minWithdraw);
    return {
      locked: minAmount > 0 && balance < minAmount,
      minAmount,
      message: `Withdrawal locked until your game balance reaches ${minAmount.toFixed(2)} FP.`,
    };
  }
  if (isTableCashout(limits)) {
    const minAmount = minCashoutAmountForLoad(limits, lastLoad);
    return {
      locked: minAmount > 0 && balance < minAmount,
      minAmount,
      message: `Withdrawal locked until your game balance reaches ${money(minAmount)}.`,
    };
  }
  const minMultiplier = minMultiplierForLoad(limits, lastLoad);
  const minAmount = lastLoad > 0 && minMultiplier > 0 ? lastLoad * minMultiplier : 0;
  return {
    locked: minAmount > 0 && balance < minAmount,
    minAmount,
    message: `Withdrawal locked until your game balance reaches ${money(minAmount)}.`,
  };
};

const getLoadLock = ({ source, lastLoadSource, activeLoad, limits, currentBalance = 0 }) => {
  const balance = Number(currentBalance || 0);
  if (balance >= 1 && lastLoadSource && source !== lastLoadSource) {
    const activeLabel = lastLoadSource === 'fp' ? 'Freeplay' : 'Main Balance';
    const targetLabel = source === 'fp' ? 'Freeplay' : 'Main Balance';
    const floorLabel = lastLoadSource === 'fp' ? '1 FP' : '$1';
    return {
      locked: true,
      message: `Finish playing your ${activeLabel} game balance below ${floorLabel} before loading ${targetLabel}.`,
    };
  }
  const maxMultiplier = maxMultiplierForLoad(limits, activeLoad);
  const tableLimit = maxCashoutAmountForLoad(limits, activeLoad);
  const payoutLimit = tableLimit > 0 ? tableLimit : (activeLoad > 0 && maxMultiplier > 0 ? activeLoad * maxMultiplier : 0);
  if (lastLoadSource === 'wallet' && payoutLimit > 0 && balance > payoutLimit) {
    return {
      locked: true,
      message: 'Your balance exceeds the payout limits. Withdraw the funds to add the next one.',
      payoutLimit,
    };
  }
  return { locked: false, message: '' };
};

function RulesCard({ mode, maxMul, lastLoad, limits, onViewRules }) {
  const isFp = mode === 'fp';
  const accent = isFp ? '#006B85' : '#075F75';
  const softBg = isFp ? '#FAF5FF' : '#ECFDF5';
  const line = isFp ? 'rgba(0,107,133,0.24)' : 'rgba(7,95,117,0.26)';
  const guide = buildWithdrawGuide({ source: mode, lastLoad, limits });
  const title = isFp ? 'Freeplay load and cashout rules' : 'Main Balance load and cashout rules';
  const loadCopy = isFp
    ? 'Loads use your Freeplay (FP) credits. This keeps bonus/freeplay play separated from normal cash loads.'
    : 'Loads use your wallet Main Balance. Game credits redeemed after play return to your wallet balance.';
	  const cashoutCopy = isFp
	    ? guide.body
	    : lastLoad > 0
	      ? `${guide.body} ${guide.footer}`
        : isTableCashout(limits)
          ? `No active load total found yet. Cashout limits follow this table: ${formatMultiplierRange(limits)}.`
	      : isThresholdUnlimited(limits)
          ? `No active load total found yet. Below $${limitNumber(limits?.highDepositMin).toFixed(2)} shows max ${limitNumber(limits?.lowDepositMaxMultiplier) || limitNumber(limits?.maxRedeemMultiplier)}x; $${limitNumber(limits?.highDepositMin).toFixed(2)}+ shows max unlimited.`
          : maxMul > 0
	        ? `No active load total found yet. Your next Main Balance load sets the cashout guide, usually up to ${maxMul}x that load.`
	        : 'No active load total found yet. Your next Main Balance load sets the cashout guide, with no fixed max cap right now.';
  const loadLimitCopy = `Add Credits: ${formatLimitRange(limits?.minLoad || 0, limits?.maxLoad || 0)}`;
  const withdrawLimitCopy = isFp
    ? `Withdraw Credits: ${formatFpAmountRange(limits?.minWithdraw || 0, limits?.maxWithdraw || 0)}`
    : `Cashout Rule: ${formatMultiplierRange(limits)}`;

  return (
    <div
      className="rounded-2xl p-3.5"
      style={{ background: softBg, border: `1px solid ${line}` }}
      data-testid={`load-rules-${mode}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: isFp ? 'rgba(0,107,133,0.14)' : 'rgba(7,95,117,0.14)' }}
        >
          <Info className="w-[18px] h-[18px]" style={{ color: accent }} strokeWidth={2.6} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="display text-sm font-extrabold" style={{ color: '#0B0F0C' }}>
            {title}
          </p>
          <p className="mt-1 text-xs leading-relaxed font-semibold" style={{ color: '#334155' }}>
            {loadCopy}
          </p>
	          <p className="mt-2 text-xs leading-relaxed font-semibold" style={{ color: isFp ? '#004D61' : '#004D61' }}>
	            {cashoutCopy}
	          </p>
	          <div
	            className="mt-2 rounded-xl px-3 py-2 text-[11px] leading-relaxed font-extrabold"
	            style={{ background: '#FFFFFF', border: `1px solid ${line}`, color: '#0B0F0C' }}
	          >
	            <p>{loadLimitCopy}</p>
	            <p>{withdrawLimitCopy}</p>
	          </div>
	        </div>
      </div>
      <button
        type="button"
        onClick={onViewRules}
        className="mt-3 w-full rounded-xl py-2.5 flex items-center justify-center gap-2 display text-sm font-extrabold transition active:scale-[0.98]"
        style={{
          background: 'linear-gradient(180deg, var(--vl-btn-warning-from, #FFE873), var(--vl-btn-warning-to, #F4B400))',
          color: 'var(--vl-btn-warning-text, #1A1100)',
          border: '1px solid rgba(244,180,0,0.45)',
        }}
        data-testid={`view-cashout-rules-${mode}`}
      >
        <Info className="w-4 h-4" strokeWidth={2.6} />
        View Full Cashout Rules
      </button>
    </div>
  );
}

function WithdrawalLiveGuide({ source, lastLoad, limits, currentBalance }) {
  const isFp = source === 'fp';
  const accent = isFp ? '#006B85' : '#EA580C';
  const guide = buildWithdrawGuide({ source, lastLoad, limits, currentBalance });
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: isFp ? 'linear-gradient(135deg, #FAF5FF, #FFFFFF)' : 'linear-gradient(135deg, #FFF7ED, #FFFFFF)',
        border: isFp ? '1px solid rgba(0,107,133,0.30)' : '1px solid rgba(249,115,22,0.30)',
      }}
      data-testid="withdraw-live-guide"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] font-extrabold" style={{ color: '#64748B' }}>
            Live withdrawal rules
          </p>
          <p className="display text-base font-extrabold mt-0.5" style={{ color: '#0B0F0C' }}>
            {guide.sourceLabel}
          </p>
        </div>
        <div
          className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide"
          style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }}
        >
          Active load {lastLoad > 0 ? money(lastLoad) : 'not found'}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="rounded-xl p-3 bg-white border border-black/5">
          <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color: '#64748B' }}>Min withdrawal</p>
          <p className="display text-lg font-extrabold mt-0.5" style={{ color: '#0B0F0C' }}>{guide.minLabel}</p>
        </div>
        <div className="rounded-xl p-3 bg-white border border-black/5">
          <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color: '#64748B' }}>Max withdrawal</p>
          <p className="display text-lg font-extrabold mt-0.5" style={{ color: '#0B0F0C' }}>{guide.maxLabel}</p>
        </div>
      </div>
      <p className="text-xs mt-3 font-bold leading-relaxed" style={{ color: isFp ? '#004D61' : '#9A3412' }}>
        {guide.body}
      </p>
      <p className="text-xs mt-1 font-semibold leading-relaxed" style={{ color: '#475569' }}>
        {guide.footer}
      </p>
    </div>
  );
}

/* -------------------- GAME ACCOUNT CARD -------------------- */
function GameAccountCard({ game, acc, wallet, cashoutRuleDocs, refreshCashoutRules, isFav, onToggleFav, refresh, onBackgroundSync, onAccountPatch, serverOperation, onGameOperation }) {
  const [refreshing, setRefreshing] = useState(false);
  const [actionBusy, setBusy] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceConfirmation, setReplaceConfirmation] = useState('');
  const replacementStorageKey = `game-replacement:${acc.user_id}:${acc.id}`;
  const [replacementPending, setReplacementPending] = useState(() => {
    try { return Boolean(localStorage.getItem(replacementStorageKey)); } catch { return false; }
  });
  const replacementInFlight = useRef(false);
  const pendingReplacement = replacementPending || ['processing', 'pending'].includes(acc.replacement_status);
  const busy = actionBusy || pendingReplacement;
  const canReplace = acc.tenant_id === 'f8418b9b-4933-4be5-9f78-79e02c3c8a5c';
  const isJennyGame = canReplace;
  const gameOperationStorageKey = `game-operation:${acc.tenant_id}:${acc.user_id}:${acc.id}`;
  const pendingGameOperation = isJennyGame && isBlockingGameOperation(serverOperation)
    ? serverOperation
    : null;
  useEffect(() => {
    // Remove markers written by older Jenny UI versions. The server's atomic
    // in-flight guard is authoritative; a browser key must never strand a card.
    if (!isJennyGame) return;
    try { localStorage.removeItem(gameOperationStorageKey); } catch { /* Private browsing may block storage. */ }
  }, [gameOperationStorageKey, isJennyGame]);
  const transactionBusy = busy || Boolean(pendingGameOperation);
  const startGameOperation = () => isJennyGame ? window.crypto.randomUUID() : null;
  const replaceAvailableAt = replacementAvailableAt(acc);
  const [replacementClock, setReplacementClock] = useState(Date.now);
  const replacementCoolingDown = replaceAvailableAt > replacementClock;
  useEffect(() => {
    setReplacementClock(Date.now());
    const remaining = replaceAvailableAt - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => setReplacementClock(Date.now()), Math.min(remaining + 50, 2147483647));
    return () => clearTimeout(timer);
  }, [replaceAvailableAt]);
  const [showPass, setShowPass] = useState(false);
  const [creditSheetOpen, setCreditSheetOpen] = useState(false);
  const [creditSheetMode, setCreditSheetMode] = useState('load'); // 'load' | 'withdraw'
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsSource, setCreditsSource] = useState('wallet'); // 'wallet' (cash $) or 'fp' (Freeplay)
  const [showCashoutRules, setShowCashoutRules] = useState(false);
  const [rulesPopupSource, setRulesPopupSource] = useState('wallet');
  const [gameRuleDocs, setGameRuleDocs] = useState(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const rulesOpenTimerRef = useRef(null);
  const depositNavigationGuardRef = useRef(0);
  // Iter 145+: pro-grade no-balance modal replaces boring toast errors.
  const [noBalModal, setNoBalModal] = useState(null); // null | 'load' | 'redeem'

  const isCreating = Boolean(acc.__creating);
  const username = isCreating ? '' : (acc.username || acc.provider_username || 'N/A');
  const password = isCreating ? '' : (acc.password || acc.provider_password || '');
  const balance = acc.last_known_balance ?? acc.balance_usd ?? 0;
  const [displayBalance, setDisplayBalance] = useState(Number(balance) || 0);
  const wholeWithdrawDisplayBalance = wholeRedeemAmount(displayBalance);
  const trailingWithdrawDisplayAmount = trailingRedeemAmount(displayBalance);
  const localBalanceSyncedAtRef = useRef(accountBalanceTimestamp(acc));
  const accountIdRef = useRef(acc.id);
  const rawImg = game.logo_image_url || game.logo_url || game.image || game.image_url || acc.image_url || 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=400&fit=crop';
  const img = rawImg && rawImg.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${rawImg}` : rawImg;
  const isCustom = acc.is_custom || acc.provider_code === 'manual' || game.is_custom;
  const normalizeFundSource = (source) => (
    String(source || '').toLowerCase() === 'fp' || String(source || '').toLowerCase() === 'freeplay'
      ? 'fp'
      : 'wallet'
  );
  const [lastLoadSource, setLastLoadSource] = useState(normalizeFundSource(acc.active_load_fund_source || acc.last_load_fund_source));
  const [lastLoadAmount, setLastLoadAmount] = useState(Number(acc.active_load_amount ?? acc.last_load_amount ?? 0));
  const walletCash = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const walletFp = Number(wallet?.play_credits ?? wallet?.fp_credits ?? wallet?.play_credits_balance ?? 0);
  const ruleGameId = game?.id || acc?.game_id || acc?.game_code;
  const effectiveCashoutRuleDocs = gameRuleDocs || cashoutRuleDocs;

  useEffect(() => {
    if (accountIdRef.current !== acc.id) {
      accountIdRef.current = acc.id;
      localBalanceSyncedAtRef.current = 0;
    }
    const incomingAt = accountBalanceTimestamp(acc);
    if (!localBalanceSyncedAtRef.current || incomingAt >= localBalanceSyncedAtRef.current) {
      localBalanceSyncedAtRef.current = Math.max(localBalanceSyncedAtRef.current || 0, incomingAt || 0);
      setDisplayBalance(Number(balance) || 0);
    }
  }, [balance, acc.id, acc.last_balance_synced_at, acc.last_synced_at, acc.updated_at]);

  useEffect(() => {
      setLastLoadSource(normalizeFundSource(acc.active_load_fund_source || acc.last_load_fund_source));
      setLastLoadAmount(Number(acc.active_load_amount ?? acc.last_load_amount ?? 0));
  }, [acc.id, acc.active_load_fund_source, acc.last_load_fund_source, acc.active_load_amount, acc.last_load_amount]);

  useEffect(() => {
    setGameRuleDocs(null);
  }, [ruleGameId]);

  useEffect(() => () => {
    if (rulesOpenTimerRef.current) {
      window.clearTimeout(rulesOpenTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const prepareForDeposit = () => {
      depositNavigationGuardRef.current = Date.now() + 1800;
      if (rulesOpenTimerRef.current) {
        window.clearTimeout(rulesOpenTimerRef.current);
        rulesOpenTimerRef.current = null;
      }
      setShowCashoutRules(false);
      setCreditSheetOpen(false);
      setNoBalModal(null);
    };
    window.addEventListener('vl:prepare-deposit', prepareForDeposit);
    return () => window.removeEventListener('vl:prepare-deposit', prepareForDeposit);
  }, []);

  const refreshEffectiveCashoutRules = useCallback(async () => {
    if (typeof refreshCashoutRules === 'function') {
      void refreshCashoutRules();
    }
    if (!ruleGameId) return;
    try {
      const [walletRules, fpRules] = await Promise.all([
        api.get(`/api/cashout-rules/${ruleGameId}?source=wallet`),
        api.get(`/api/cashout-rules/${ruleGameId}?source=fp`),
      ]);
      setGameRuleDocs({
        wallet: walletRules.data || null,
        fp: fpRules.data || null,
      });
    } catch {
      setGameRuleDocs(null);
    }
  }, [refreshCashoutRules, ruleGameId]);

  const syncBackgroundBalances = async (options = {}) => {
    if (typeof onBackgroundSync === 'function') {
      await onBackgroundSync({ force: true, ...options });
    }
  };

  const applyBalancePayload = (payload = {}) => {
    const nextBalance = Number(payload.balance_usd ?? payload.balance_sc ?? payload.balance ?? displayBalance ?? 0);
    const safeBalance = Number.isFinite(nextBalance) ? nextBalance : 0;
    const syncedAt = Date.parse(payload.last_balance_synced_at || payload.last_synced_at || '');
    localBalanceSyncedAtRef.current = Math.max(
      localBalanceSyncedAtRef.current || 0,
      Number.isFinite(syncedAt) ? syncedAt : Date.now(),
    );
    setDisplayBalance(safeBalance);

    const payloadSource = payload.active_load_fund_source ?? payload.active_load_source ?? payload.last_load_fund_source ?? payload.fund_source_at_load;
    const nextLastLoadSource = payloadSource ? normalizeFundSource(payloadSource) : lastLoadSource;
    const nextLastLoadAmount = Number(payload.active_load_amount ?? payload.last_load_amount ?? lastLoadAmount ?? 0) || 0;
    if (payloadSource) setLastLoadSource(nextLastLoadSource);
    if (payload.last_load_amount !== undefined) setLastLoadAmount(nextLastLoadAmount);
    return {
      balance: safeBalance,
      lastLoadSource: nextLastLoadSource,
      lastLoadAmount: nextLastLoadAmount,
    };
  };

  const refreshBalance = async ({ silent = false } = {}) => {
    if (isCreating) return { balance: displayBalance, lastLoadSource, lastLoadAmount };
    setRefreshing(true);
    try {
      const res = await api.post(`/api/games/accounts/${acc.id}/check-balance`);
      const nextState = applyBalancePayload(res.data || {});
      if (!silent) {
        toast.success(`Balance refreshed: $${nextState.balance.toFixed(2)}`);
      }
      if (!silent) void syncBackgroundBalances();
      return nextState;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Failed to refresh');
      return null;
    } finally { setRefreshing(false); }
  };

  const openSupportForPasswordReset = () => {
    setResetConfirmOpen(false);
    window.dispatchEvent(new CustomEvent('vl:open-support', {
      detail: {
        reason: 'game-password-reset',
        accountId: acc.id,
        gameName: game.name,
      },
    }));
  };

	  const submitAddCredits = async () => {
	    if (transactionBusy) return;
	    const amt = parseFloat(creditsAmount);
	    if (!amt || amt <= 0) { quickGameToast('error', 'Enter $ amount'); return; }
	    const limits = sourceLimits(effectiveCashoutRuleDocs, creditsSource);
	    const sourceLabel = creditsSource === 'fp' ? 'Freeplay' : 'Main Balance';
	    if (limits.minLoad > 0 && amt < limits.minLoad) {
	      quickGameToast('error', `Minimum ${sourceLabel} game load is $${limits.minLoad.toFixed(2)}`);
	      return;
	    }
	    if (limits.maxLoad > 0 && amt > limits.maxLoad) {
	      quickGameToast('error', `Maximum ${sourceLabel} game load is $${limits.maxLoad.toFixed(2)}`);
	      return;
	    }

	    // Iter 145+: client-side guard so the player sees the polished modal
    // (with the Deposit Now CTA) instead of a generic API error toast.
    const fundsAvailable = creditsSource === 'fp' ? walletFp : walletCash;
    if (fundsAvailable <= 0 || fundsAvailable < amt) {
      const walletCanCover = creditsSource !== 'wallet' && walletCash >= amt;
      const fpCanCover = creditsSource !== 'fp' && walletFp >= amt;
      if (walletCanCover || fpCanCover) {
        const nextSource = walletCanCover ? 'wallet' : 'fp';
        const switchLock = getLoadLock({
          source: nextSource,
          lastLoadSource,
          activeLoad: lastLoadAmount,
          limits: sourceLimits(effectiveCashoutRuleDocs, nextSource),
          currentBalance: displayBalance,
        });
        if (switchLock.locked) {
          quickGameToast('error', switchLock.message);
          return;
        }
        setCreditsSource(nextSource);
        quickGameToast('info', `Switched to ${nextSource === 'fp' ? 'Freeplay' : 'Main Balance'} for this load`);
        return;
      }
      setNoBalModal('load');
      return;
    }

    let operationStarted = false;
    setBusy(true);
    try {
      const liveState = await refreshBalance({ silent: true });
      const currentBalance = Number(liveState?.balance ?? displayBalance ?? 0);
      const effectiveLastLoadSource = liveState?.lastLoadSource || lastLoadSource;
      const effectiveActiveLoadAmount = Number(liveState?.lastLoadAmount ?? lastLoadAmount ?? 0) || 0;
      const liveLoadLock = getLoadLock({
        source: creditsSource,
        lastLoadSource: effectiveLastLoadSource,
        activeLoad: effectiveActiveLoadAmount,
        limits,
        currentBalance,
      });
      if (liveLoadLock.locked) {
        quickGameToast('error', liveLoadLock.message);
        return;
      }

      const operationKey = startGameOperation('load');
      if (isJennyGame && !operationKey) return;
      operationStarted = Boolean(operationKey);
      const res = await api.post(
        '/api/games/load',
        { game_id: game.id, account_id: acc.id, amount: amt, fund_source: creditsSource },
        { headers: { 'X-Idempotency-Key': operationKey || `load-${acc.id}-${Date.now()}` } }
      );
      if (res.status === 202) {
        onGameOperation?.(res.data?.operation);
        toast.info(res.data?.message || 'Your game load is processing. Please wait.', { id: GAME_ACTION_TOAST_ID });
        return;
      }
      const rawNextBalance = res.data?.provider_new_balance;
      const nextBalance = rawNextBalance === null || rawNextBalance === undefined || rawNextBalance === ''
        ? NaN
        : Number(rawNextBalance);
      const syncedAt = res.data?.last_balance_synced_at || new Date().toISOString();
      if (Number.isFinite(nextBalance)) {
        const accountPatch = {
          balance_usd: nextBalance,
          last_known_balance: nextBalance,
          active_load_fund_source: res.data?.active_load_fund_source || res.data?.last_load_fund_source || creditsSource,
          active_load_amount: res.data?.active_load_amount ?? res.data?.last_load_amount ?? amt,
          last_load_fund_source: res.data?.last_load_fund_source || creditsSource,
          last_load_amount: res.data?.last_load_amount ?? amt,
          last_load_at: res.data?.last_load_at || syncedAt,
          last_balance_synced_at: syncedAt,
        };
        applyBalancePayload(accountPatch);
        onAccountPatch?.(acc.id, accountPatch);
      } else {
        const refreshed = await refreshBalance({ silent: true });
        if (refreshed) {
          onAccountPatch?.(acc.id, {
            balance_usd: refreshed.balance,
            last_known_balance: refreshed.balance,
            active_load_fund_source: refreshed.lastLoadSource,
            active_load_amount: refreshed.lastLoadAmount,
            last_load_fund_source: refreshed.lastLoadSource,
            last_load_amount: refreshed.lastLoadAmount,
            last_balance_synced_at: new Date().toISOString(),
          });
        }
      }
      quickGameToast('success', `Load successful — $${amt.toFixed(2)} added to ${acc.game_name || acc.name || 'your game'}`);
      setLastLoadSource(normalizeFundSource(res.data?.active_load_fund_source || res.data?.last_load_fund_source || creditsSource));
      setLastLoadAmount(Number(res.data?.active_load_amount ?? res.data?.last_load_amount ?? amt) || amt);
      setCreditsAmount('');
      setCreditSheetOpen(false);
      void syncBackgroundBalances({ walletOnly: true });
    } catch (err) {
      const actionError = gameActionError(err, 'Failed to add credits');
      const detail = err?.response?.data?.detail;
      if (operationStarted && actionError.code === 'provider_outcome_pending') {
        toast.info('We are checking this load. Please do not load again; contact support if your game balance does not update.', { id: GAME_ACTION_TOAST_ID });
      } else if (operationStarted && (!err?.response || err.response.status >= 500) && !confirmedSafeGameFailure(detail)) {
        toast.info('Load outcome is unknown. Check your game balance and contact support before repeating it.', { id: GAME_ACTION_TOAST_ID });
      } else if (actionError.code === 'wallet_insufficient_funds') {
        setNoBalModal('load');
      } else {
        quickGameToast('error', actionError.message);
      }
    } finally { setBusy(false); }
  };

	  const submitWithdraw = async () => {
	    if (transactionBusy) return;
	    const liveState = await refreshBalance({ silent: true });
	    const currentBalance = Number(liveState?.balance ?? displayBalance ?? 0);
      const currentWholeBalance = wholeRedeemAmount(currentBalance);
	    const effectiveLastLoadSource = liveState?.lastLoadSource || lastLoadSource;
	    const effectiveLastLoadAmount = Number(liveState?.lastLoadAmount ?? lastLoadAmount ?? 0) || 0;
	    // Iter 145+: short-circuit when game balance is $0 → polished modal.
	    if (!currentBalance || currentBalance <= 0) {
	      setNoBalModal('redeem');
	      setCreditSheetOpen(false);
	      return;
	    }
      if (currentWholeBalance <= 0) {
        quickGameToast('success', CASHOUT_SUCCESS_MESSAGE);
        setCreditSheetOpen(false);
        return;
      }
		    const limits = sourceLimits(effectiveCashoutRuleDocs, effectiveLastLoadSource);
		    const lock = getWithdrawLock({
		      source: effectiveLastLoadSource,
		      lastLoad: effectiveLastLoadAmount,
		      limits,
		      currentBalance: currentWholeBalance,
		    });
		    if (lock.locked) {
		      quickGameToast('error', lock.message);
		      return;
		    }

	    let operationStarted = false;
	    setBusy(true);
    try {
	      const operationKey = startGameOperation('redeem');
	      if (isJennyGame && !operationKey) return;
	      operationStarted = Boolean(operationKey);
	      const res = await api.post('/api/games/redeem', { game_id: game.id, account_id: acc.id },
	        operationKey ? { headers: { 'X-Idempotency-Key': operationKey } } : undefined);
      if (res.status === 202) {
        onGameOperation?.(res.data?.operation);
        toast.info(res.data?.message || 'Your game redeem is processing. Please wait.', { id: GAME_ACTION_TOAST_ID });
        return;
      }
      const nextBalance = Number(res.data?.game_balance_after ?? 0);
      applyBalancePayload({
        balance_usd: Number.isFinite(nextBalance) ? nextBalance : 0,
        active_load_amount: Number.isFinite(nextBalance) && nextBalance >= 1 ? effectiveLastLoadAmount : 0,
        active_load_fund_source: Number.isFinite(nextBalance) && nextBalance >= 1 ? (res.data?.fund_source_at_load || effectiveLastLoadSource) : null,
        last_load_amount: 0,
        last_load_fund_source: res.data?.fund_source_at_load || effectiveLastLoadSource,
        last_synced_at: new Date().toISOString(),
      });
      onAccountPatch?.(acc.id, {
        balance_usd: Number.isFinite(nextBalance) ? nextBalance : 0,
        last_known_balance: Number.isFinite(nextBalance) ? nextBalance : 0,
        active_load_amount: Number.isFinite(nextBalance) && nextBalance >= 1 ? effectiveLastLoadAmount : 0,
        active_load_fund_source: Number.isFinite(nextBalance) && nextBalance >= 1 ? (res.data?.fund_source_at_load || effectiveLastLoadSource) : null,
        last_load_amount: 0,
        last_load_fund_source: res.data?.fund_source_at_load || effectiveLastLoadSource,
        last_redeem_at: new Date().toISOString(),
        last_balance_synced_at: new Date().toISOString(),
      });
      quickGameToast('success', res.data.message || CASHOUT_SUCCESS_MESSAGE);
      setCreditSheetOpen(false);
      void syncBackgroundBalances({ walletOnly: true });
    } catch (err) {
      const actionError = gameActionError(err, 'Failed to withdraw');
      const detail = err?.response?.data?.detail;
      if (operationStarted && actionError.code === 'provider_outcome_pending') {
        toast.info('This redeem needs provider review. Check your wallet and game balance, then contact support before repeating it.', { id: GAME_ACTION_TOAST_ID });
      } else if (operationStarted && (!err?.response || err.response.status >= 500) && !confirmedSafeGameFailure(detail)) {
        toast.info('Redeem outcome is unknown. Check your wallet and game balance, then contact support before repeating it.', { id: GAME_ACTION_TOAST_ID });
      } else {
        quickGameToast('error', actionError.message);
      }
    } finally { setBusy(false); }
  };

  const playNow = () => {
    if (game.download_url) {
      window.open(game.download_url, '_blank', 'noopener,noreferrer');
    } else {
      toast.info('Game URL not configured');
    }
  };

  const replaceAccount = async () => {
    if (replacementInFlight.current || busy || replaceAvailableAt > Date.now() || replaceConfirmation !== 'CONFIRM') return;
    replacementInFlight.current = true;
    setBusy(true);
    let key;
    try {
      // Store before sending: a timeout/reload must never create a second
      // replacement. No automatic replay or undeployed status polling.
      key = window.crypto.randomUUID();
      localStorage.setItem(replacementStorageKey, key);
    } catch {
      toast.error('Enable browser storage before replacing your account.');
      replacementInFlight.current = false;
      setBusy(false);
      return;
    }
    setReplacementPending(true);
    try {
      const res = await api.post('/api/games/replace-account', {
        game_id: acc.game_id || game.id, account_id: acc.id, confirmation: replaceConfirmation,
      }, { headers: { 'X-Idempotency-Key': key }, timeout: 120000 });
      if (res.data?.final_status === 'completed' && res.data?.account) {
        localStorage.removeItem(replacementStorageKey);
        setReplacementPending(false);
        setReplaceOpen(false);
        setShowPass(false);
        onAccountPatch?.(acc.id, res.data.account);
        toast.success('New game account ready. Loads and withdrawals now use the new account.');
        void Promise.resolve(refresh?.()).catch(() => toast.info('Account replaced. Refresh your games to see the new card.'));
      } else {
        toast.info('Provider outcome is being verified. Contact support; do not replace again.');
      }
    } catch (err) {
      const status = err.response?.status;
      // Only explicit pre-provider rejections are safe to start over. A 409,
      // timeout or 5xx can be an in-progress operation and stays blocked.
      if ([400, 401, 403, 404, 422].includes(status)) {
        localStorage.removeItem(replacementStorageKey);
        setReplacementPending(false);
        toast.error(err.response?.data?.detail || 'Replacement was not started.');
      } else {
        toast.info(err.response?.data?.detail || 'Provider outcome is being verified. Contact support.');
      }
    } finally {
      replacementInFlight.current = false;
      setBusy(false);
    }
  };

	  const openLoadSheet = async () => {
	    setCreditSheetMode('load');
      setCreditsSource(Number(displayBalance || 0) >= 1 ? lastLoadSource : (walletCash > 0 || walletFp <= 0 ? 'wallet' : 'fp'));
	    setRulesLoading(true);
	    try {
	      const [, liveState] = await Promise.all([
	        refreshEffectiveCashoutRules(),
	        refreshBalance({ silent: true }),
	      ]);
        if (Number(liveState?.balance || 0) >= 1 && liveState?.lastLoadSource) {
          setCreditsSource(liveState.lastLoadSource);
        }
	    } finally {
	      setRulesLoading(false);
	      setCreditSheetOpen(true);
	    }
	  };

	  const openWithdrawSheet = async () => {
	    setCreditsSource(lastLoadSource);
	    setCreditSheetMode('withdraw');
	    setRulesLoading(true);
	    try {
	      await Promise.all([
	        refreshEffectiveCashoutRules(),
	        refreshBalance({ silent: true }),
	      ]);
	    } finally {
	      setRulesLoading(false);
	      setCreditSheetOpen(true);
	    }
	  };

  const openRulesForSource = (source) => {
    if (Date.now() < depositNavigationGuardRef.current) return;
    const nextSource = normalizeFundSource(source);
    setRulesPopupSource(nextSource);
    setCreditSheetOpen(false);
    if (rulesOpenTimerRef.current) {
      window.clearTimeout(rulesOpenTimerRef.current);
    }
    rulesOpenTimerRef.current = window.setTimeout(() => {
      rulesOpenTimerRef.current = null;
      if (Date.now() >= depositNavigationGuardRef.current) {
        setShowCashoutRules(true);
      }
    }, 180);
  };

  const closeNoBalanceModal = () => {
    setNoBalModal(null);
  };

  const goToDepositFromNoBalance = () => {
    depositNavigationGuardRef.current = Date.now() + 1800;
    if (rulesOpenTimerRef.current) {
      window.clearTimeout(rulesOpenTimerRef.current);
      rulesOpenTimerRef.current = null;
    }
    setNoBalModal(null);
    setShowCashoutRules(false);
    setCreditSheetOpen(false);
    window.dispatchEvent(new CustomEvent('vl:prepare-deposit'));
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('vl:open-deposit'));
    }, 80);
  };

		  const maxMul = Number(acc.redeem_max_multiplier ?? game.redeem_max_multiplier ?? 10) || 10;
		  const lastLoad = Number(lastLoadAmount ?? 0);
		  const withdrawSourceLabel = lastLoadSource === 'fp' ? 'Freeplay (FP)' : 'Main Balance';
  const loadLimits = sourceLimits(effectiveCashoutRuleDocs, creditsSource);
  const withdrawLimits = sourceLimits(effectiveCashoutRuleDocs, lastLoadSource);
  const withdrawLock = getWithdrawLock({
    source: lastLoadSource,
    lastLoad,
    limits: withdrawLimits,
    currentBalance: wholeWithdrawDisplayBalance,
  });
  const activeMaxMul = lastLoadSource === 'fp'
    ? maxMul
    : maxMultiplierForLoad(withdrawLimits, lastLoad);
  const selectedMaxMul = creditsSource === 'fp'
    ? maxMul
    : maxMultiplierForLoad(loadLimits, lastLoad);
  const loadLock = getLoadLock({
    source: creditsSource,
    lastLoadSource,
    activeLoad: lastLoad,
    limits: loadLimits,
    currentBalance: displayBalance,
  });
  const loadLockedByGameBalance = loadLock.locked;

  return (
    <div
      className="relative rounded-3xl overflow-hidden"
      style={{
        background: '#FFFFFF',
        border: '1px solid rgba(0,77,97,0.35)',
        boxShadow: '0 12px 36px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,77,97,0.1), 0 0 30px rgba(0,77,97,0.18)'
      }}
      data-testid={`my-game-${game.id}`}
    >
      {/* Top: image + info */}
      <div className="p-3 sm:p-4 flex gap-3">
        <div
          className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 6px 14px rgba(0,0,0,0.08)' }}
        >
          <SafeImage
            src={img}
            alt={game.name}
            className="w-full h-full object-cover"
            loading="lazy"
            fallback={<div className="w-full h-full flex items-center justify-center bg-black text-2xl font-black text-white">{game.name?.charAt(0) || '?'}</div>}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            {isCreating ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ color: '#6B7280' }} />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#075F75' }} />
            )}
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: isCreating ? '#6B7280' : '#075F75' }}>
              {isCreating ? 'Creating' : 'Active'}
            </span>
          </div>
          <h3 className="display text-xl sm:text-2xl font-extrabold leading-tight truncate" style={{ color: '#0B0F0C' }}>{game.name}</h3>
          {isCustom ? (
            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md" style={{ background: 'rgba(125,211,252,0.18)', color: '#0369A1', border: '1px solid rgba(3,105,161,0.4)' }}>
              Custom
            </span>
          ) : (
            <p className="display text-xl font-extrabold mt-0.5" style={{ color: '#0B0F0C' }}>${Number(displayBalance).toFixed(2)}</p>
          )}

          <div className="flex items-start gap-2 mt-2">
            {!isCustom && (
              <div className="min-w-0">
	                <button
	                  onClick={refreshBalance}
	                  disabled={refreshing || isCreating}
	                  className="display font-bold text-[11px] sm:text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 transition"
	                  style={{
                      background: isCreating ? '#D1D5DB' : '#0B0F0C',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.06)',
                      cursor: isCreating ? 'not-allowed' : 'pointer',
                    }}
	                  data-testid={`refresh-balance-${acc.id}`}
	                >
	                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
	                  {refreshing ? 'Refreshing…' : 'Refresh Balance'}
                </button>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'rgba(11,15,12,0.56)' }}>
                  Current balance: ${Number(displayBalance).toFixed(2)}
                </p>
              </div>
            )}
            {isCustom && (
              <button
                onClick={async () => {
                  if (!window.confirm('Remove this custom game from your list?')) return;
                  try {
                    await api.delete(`/api/games/accounts/${acc.id}`);
                    toast.success('Removed');
                    refresh();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to remove');
                  }
                }}
                className="display font-bold text-[11px] sm:text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 transition"
                style={{ background: 'rgba(239,68,68,0.1)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.35)' }}
                data-testid={`remove-custom-${acc.id}`}
              >
                <XCircle className="w-3.5 h-3.5" /> Remove
              </button>
            )}
	            <button
	              onClick={isCreating ? undefined : onToggleFav}
                disabled={isCreating}
	              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition"
	              style={{
	                background: isFav
	                  ? 'radial-gradient(circle at 30% 30%, #FFE48A, #FFC53D 60%, #B07A00)'
	                  : '#F3F4F6',
	                border: isFav ? '1px solid rgba(255,213,61,0.5)' : '1px solid rgba(0,0,0,0.08)',
	                boxShadow: isFav ? '0 0 14px rgba(255,213,61,0.5)' : 'none',
                  opacity: isCreating ? 0.7 : 1,
                  cursor: isCreating ? 'not-allowed' : 'pointer',
	              }}
              data-testid={`fav-${acc.id}`}
              title={isFav ? 'Unfavorite' : 'Favorite'}
            >
              <Star className="w-4 h-4" style={{ color: isFav ? '#031A2A' : '#9CA3AF', fill: isFav ? '#031A2A' : 'transparent' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Middle: Game ID + Password */}
      {isCreating ? (
        <div className="mx-3 sm:mx-4 mb-3 rounded-xl p-5 text-center" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.05)' }}>
          <div className="flex items-center justify-center gap-3 py-4" aria-label="Creating account">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="creating-game-dot block w-4 h-4 rounded-full"
                style={{ animationDelay: `${dot * 0.16}s` }}
              />
            ))}
          </div>
          <p className="text-[11px] font-bold" style={{ color: '#C4C8D0' }}>
            Creating your game ID...
          </p>
        </div>
      ) : (
      <div className="mx-3 sm:mx-4 mb-3 rounded-xl p-3" style={{ background: '#F1F3F5', border: '1px solid rgba(0,0,0,0.05)' }}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Gamepad2 className="w-4 h-4 shrink-0" style={{ color: '#3B82F6' }} />
            <span className="text-sm font-semibold" style={{ color: '#0B0F0C' }}>Game ID:</span>
            <span
              className="text-sm font-mono font-bold truncate"
              style={{ color: '#0B0F0C' }}
              data-testid={`game-id-${acc.id}`}
              title={username}
            >
              {username}
            </span>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(username); toast.success('Game ID copied'); }}
            className="shrink-0 p-1.5 rounded-lg transition"
            style={{ background: 'rgba(99,102,241,0.1)', color: '#6366F1' }}
            data-testid={`copy-id-${acc.id}`}
            title="Copy Game ID"
            aria-label="Copy Game ID"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="mt-2 pt-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <KeyRound className="w-4 h-4 shrink-0" style={{ color: '#F59E0B' }} />
            <span className="text-sm font-semibold" style={{ color: '#0B0F0C' }}>Password:</span>
            {!showPass && (
              <span
                className="text-sm font-mono font-bold tracking-widest"
                style={{ color: '#0B0F0C' }}
                data-testid={`game-pw-${acc.id}`}
              >
                •••••••
              </span>
            )}
            {password && (
              <button
                onClick={() => setShowPass(!showPass)}
                className="shrink-0 p-1 rounded transition"
                style={{ color: '#6B7280' }}
                title={showPass ? 'Hide password' : 'Show password'}
                aria-label={showPass ? 'Hide password' : 'Show password'}
                data-testid={`toggle-pw-${acc.id}`}
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
            {password && (
              <button
                onClick={() => { navigator.clipboard.writeText(password); toast.success('Password copied'); }}
                className="shrink-0 p-1.5 rounded-lg transition"
                style={{ background: 'rgba(245,158,11,0.12)', color: '#B45309' }}
                title="Copy password"
                aria-label="Copy password"
                data-testid={`copy-pw-${acc.id}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {showPass && password && (
            <div
              className="mt-2 px-2.5 py-1.5 rounded-lg font-mono text-sm font-bold break-all"
              style={{ background: 'rgba(245,158,11,0.10)', border: '1px dashed rgba(245,158,11,0.45)', color: '#0B0F0C' }}
              data-testid={`game-pw-${acc.id}`}
            >
              {password}
            </div>
          )}

          {!isCustom && (
            <button
              onClick={() => setResetConfirmOpen(true)}
              disabled={busy}
              className="mt-2 w-full display font-bold text-xs py-2 rounded-full flex items-center justify-center gap-1.5 transition active:scale-[0.99]"
              style={{
                background: '#0B0F0C',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.06)',
                opacity: busy ? 0.6 : 1,
              }}
              data-testid={`reset-game-pw-${acc.id}`}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Reset Password
            </button>
          )}
          {!isCustom && canReplace && (
            <button
              onClick={() => { setReplaceConfirmation(''); setReplaceOpen(true); }}
              disabled={busy || refreshing || replacementCoolingDown}
              className="mt-2 w-full font-bold text-xs py-2 rounded-full border border-red-300 bg-red-50 text-red-700 disabled:opacity-50"
              data-testid={`replace-game-account-${acc.id}`}
            >
              {pendingReplacement ? 'Replacement pending — contact support' : replacementCoolingDown ? 'Replace Account — 7-day cooldown' : 'Replace Account'}
            </button>
          )}
          {!isCustom && canReplace && replacementCoolingDown && (
            <p className="mt-2 text-xs text-center text-slate-600" role="status">
              Replace available {new Date(replaceAvailableAt).toLocaleString()}. Loads and withdrawals remain available.
            </p>
          )}
        </div>

        {isCustom && (
          <p className="text-[10px] mt-2 text-center" style={{ color: '#6B7280' }}>
            (Custom game — credentials are saved locally for your reference)
          </p>
        )}

      </div>
      )}

      <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2">
        {!isCustom && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={isCreating ? undefined : openLoadSheet}
              disabled={rulesLoading || isCreating}
              className="display rounded-xl py-2.5 px-3 flex items-center justify-center gap-1.5 font-extrabold text-sm transition active:scale-[0.98]"
              style={{
                background: isCreating ? '#D1D5DB' : 'linear-gradient(180deg, var(--vl-btn-add-credits-from, #5B2BFF), var(--vl-btn-add-credits-to, #3614C9))',
                color: 'var(--vl-btn-add-credits-text, #fff)',
                boxShadow: isCreating ? 'none' : '0 8px 22px rgba(91,43,255,0.4), inset 0 1px 0 rgba(255,255,255,0.25)',
                cursor: isCreating ? 'not-allowed' : 'pointer',
              }}
              data-testid={`add-credits-${acc.id}`}
            >
              {rulesLoading && creditSheetMode === 'load' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowDownCircle className="w-4 h-4" />}
              {rulesLoading && creditSheetMode === 'load' ? 'Loading Rules' : 'Add Credits'}
            </button>
            <button
              onClick={isCreating ? undefined : openWithdrawSheet}
              disabled={rulesLoading || isCreating}
              className="display rounded-xl py-2.5 px-3 flex items-center justify-center gap-1.5 font-extrabold text-sm transition active:scale-[0.98]"
              style={{
                background: isCreating ? '#D1D5DB' : 'linear-gradient(180deg, var(--vl-btn-withdraw-from, #FF8A3D), var(--vl-btn-withdraw-to, #E63946))',
                color: 'var(--vl-btn-withdraw-text, #fff)',
                boxShadow: isCreating ? 'none' : '0 8px 22px rgba(230,57,70,0.4), inset 0 1px 0 rgba(255,255,255,0.25)',
                cursor: isCreating ? 'not-allowed' : 'pointer',
              }}
              data-testid={`withdraw-credits-${acc.id}`}
            >
              {rulesLoading && creditSheetMode === 'withdraw' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowUpCircle className="w-4 h-4" />}
              {rulesLoading && creditSheetMode === 'withdraw' ? 'Loading Rules' : 'Withdraw Credits'}
            </button>
          </div>
        )}
        <button
          onClick={isCreating ? undefined : playNow}
          disabled={isCreating}
          className="display w-full rounded-xl py-3 flex items-center justify-center gap-2 font-extrabold text-base transition active:scale-[0.98]"
          style={{
            background: isCreating ? '#D1D5DB' : 'linear-gradient(180deg, var(--vl-btn-primary-from, #004D61), var(--vl-btn-primary-to, #004D61))',
            color: 'var(--vl-btn-primary-text, #031A2A)',
            boxShadow: isCreating ? 'none' : '0 0 0 1px rgba(0,77,97,0.45), 0 12px 28px rgba(0,77,97,0.4), inset 0 1px 0 rgba(255,255,255,0.5)',
            cursor: isCreating ? 'not-allowed' : 'pointer',
          }}
          data-testid={`play-now-${acc.id}`}
        >
          <Gamepad2 className="w-4 h-4" />
          Play Now
        </button>
      </div>

      <Sheet open={creditSheetOpen} onOpenChange={setCreditSheetOpen}>
        <SheetContent
          side="bottom"
          overlayClassName="bg-black/72 backdrop-blur-md"
          overlayStyle={{ zIndex: 70 }}
          className="player-app p-0 rounded-t-[28px] border-x-0 border-b-0 h-[64dvh] max-h-[660px] overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
            borderColor: 'rgba(0,77,97,0.30)',
            color: '#0B0F0C',
            zIndex: 75,
          }}
        >
          <div className="flex flex-col h-full">
            <SheetHeader className="px-5 pt-5 pb-3 pr-16 text-left border-b border-black/5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-extrabold" style={{ color: creditSheetMode === 'load' ? '#075F75' : '#EA580C' }}>
                {creditSheetMode === 'load' ? 'Game Load' : 'Game Withdrawal'}
              </p>
              <SheetTitle
                className="display text-2xl sm:text-3xl font-extrabold leading-tight"
                style={{ color: '#0B0F0C' }}
                data-testid={`credits-sheet-title-${acc.id}`}
              >
                {creditSheetMode === 'load'
                  ? `Add Credits To ${game.name}`
                  : `Withdraw Credits From ${game.name}`}
              </SheetTitle>
              <p className="text-xs font-semibold" style={{ color: '#64748B' }}>
                Current game balance: ${Number(displayBalance).toFixed(2)}
              </p>
            </SheetHeader>

            <div className="overflow-y-auto px-5 py-4 space-y-4">
              {pendingGameOperation && !actionBusy && (
                <p role="status" className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-950">
                  Your game transaction is processing. Please wait for it to finish.
                </p>
              )}
              {creditSheetMode === 'load' ? (
                <div
                  className="relative space-y-4"
                  aria-busy={transactionBusy ? 'true' : 'false'}
                  data-testid={`load-panel-${acc.id}`}
                >
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setCreditsSource('wallet')}
                      disabled={transactionBusy}
                      className="rounded-2xl px-3 py-3 text-left transition active:scale-[0.99]"
                      style={{
                        background: creditsSource === 'wallet' ? 'linear-gradient(135deg, #DCFCE7, #BBF7D0)' : '#FFFFFF',
                        border: creditsSource === 'wallet' ? '2px solid #075F75' : '1px solid rgba(0,0,0,0.1)',
                        boxShadow: creditsSource === 'wallet' ? '0 4px 12px rgba(7,95,117,0.18)' : 'none',
                        opacity: transactionBusy ? 0.68 : 1,
                        cursor: transactionBusy ? 'not-allowed' : 'pointer',
                      }}
                      data-testid={`fund-source-wallet-${acc.id}`}
                    >
                      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#15803D' }}>Main Balance</p>
                      <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>
                        ${walletCash.toFixed(2)}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreditsSource('fp')}
                      disabled={transactionBusy}
                      className="rounded-2xl px-3 py-3 text-left transition active:scale-[0.99]"
                      style={{
                        background: creditsSource === 'fp' ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)' : '#FFFFFF',
                        border: creditsSource === 'fp' ? '2px solid #F59E0B' : '1px solid rgba(0,0,0,0.1)',
                        boxShadow: creditsSource === 'fp' ? '0 4px 12px rgba(245,158,11,0.18)' : 'none',
                        opacity: transactionBusy ? 0.68 : 1,
                        cursor: transactionBusy ? 'not-allowed' : 'pointer',
                      }}
                      data-testid={`fund-source-fp-${acc.id}`}
                    >
                      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#92400E' }}>Freeplay (FP)</p>
                      <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>
                        {Math.floor(walletFp).toLocaleString()} FP
                      </p>
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="$ amount"
                      value={creditsAmount}
                      onChange={e => setCreditsAmount(e.target.value)}
                      disabled={transactionBusy}
                      className="h-12 mono flex-1"
                      style={{ background: '#fff', borderColor: 'rgba(15,23,42,0.12)', color: '#0B0F0C' }}
                      data-testid={`credits-amount-${acc.id}`}
                    />
                    <Button
                      onClick={submitAddCredits}
                      disabled={transactionBusy || loadLockedByGameBalance}
                      className="h-12 px-5 display font-bold rounded-xl"
                      style={{
                        background: pendingGameOperation || loadLockedByGameBalance
                          ? 'linear-gradient(180deg, #CBD5E1, #94A3B8)'
                          : creditsSource === 'fp'
                          ? 'linear-gradient(180deg, #F59E0B, #B45309)'
                          : 'linear-gradient(180deg, #075F75, #047857)',
                        color: pendingGameOperation || loadLockedByGameBalance ? '#334155' : '#fff',
                      }}
                      data-testid={`confirm-load-${acc.id}`}
	                    >
	                      {busy ? (
	                        <span className="flex items-center gap-2">
	                          <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={2.8} />
	                          Loading
	                        </span>
	                      ) : (pendingGameOperation ? 'Processing' : (loadLockedByGameBalance ? 'Locked' : 'Load'))}
	                    </Button>
	                  </div>
	                  {busy && (
	                    <div
	                      className="rounded-2xl px-4 py-4 flex items-center gap-3 overflow-hidden"
	                      style={{
	                        background: creditsSource === 'fp'
	                          ? 'linear-gradient(135deg, rgba(245,158,11,0.16), rgba(255,255,255,0.94))'
	                          : 'linear-gradient(135deg, rgba(7,95,117,0.16), rgba(255,255,255,0.94))',
	                        border: creditsSource === 'fp'
	                          ? '1px solid rgba(245,158,11,0.35)'
	                          : '1px solid rgba(7,95,117,0.35)',
	                        boxShadow: creditsSource === 'fp'
	                          ? '0 10px 28px rgba(245,158,11,0.16)'
	                          : '0 10px 28px rgba(7,95,117,0.16)',
	                      }}
	                      data-testid={`load-processing-${acc.id}`}
	                    >
	                      <div
	                        className="relative shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
	                        style={{
	                          background: creditsSource === 'fp'
	                            ? 'linear-gradient(180deg, #FDE68A, #F59E0B)'
	                            : 'linear-gradient(180deg, #BBF7D0, #075F75)',
	                          color: '#0B0F0C',
	                          boxShadow: creditsSource === 'fp'
	                            ? '0 0 24px rgba(245,158,11,0.55)'
	                            : '0 0 24px rgba(7,95,117,0.50)',
	                        }}
	                      >
	                        <span
	                          className="absolute inset-0 rounded-full animate-ping"
	                          style={{ background: creditsSource === 'fp' ? 'rgba(245,158,11,0.22)' : 'rgba(7,95,117,0.22)' }}
	                        />
	                        <RefreshCw className="relative w-6 h-6 animate-spin" strokeWidth={2.8} />
	                      </div>
	                      <div className="min-w-0">
	                        <p className="display text-sm font-extrabold" style={{ color: '#0B0F0C' }}>
	                          Loading credits...
	                        </p>
	                        <p className="text-xs mt-0.5 font-semibold leading-relaxed" style={{ color: '#64748B' }}>
	                          Keep this open. Your game balance updates as soon as the provider finishes.
	                        </p>
	                      </div>
	                    </div>
	                  )}
	                  {loadLockedByGameBalance && (
	                    <div
	                      className="rounded-2xl px-4 py-3 text-xs font-bold leading-relaxed"
	                      style={{ background: '#FEF2F2', border: '1px solid rgba(239,68,68,0.30)', color: '#991B1B' }}
	                      data-testid={`load-balance-lock-${acc.id}`}
	                    >
	                      {loadLock.message || `Add credits unlocks when this game balance is below $1. Current live balance is ${Number(displayBalance).toFixed(2)}.`}
	                    </div>
	                  )}
	                  <p className="text-[11px] font-bold" style={{ color: '#64748B' }}>
	                    {creditsSource === 'fp' ? 'Freeplay' : 'Main Balance'} load limit: {formatLimitRange(loadLimits.minLoad, loadLimits.maxLoad)}
	                  </p>

			                  <RulesCard
			                    mode={creditsSource}
			                    maxMul={selectedMaxMul}
			                    lastLoad={lastLoad}
			                    limits={loadLimits}
			                    onViewRules={() => openRulesForSource(creditsSource)}
			                  />
	                </div>
		              ) : (
		                <>
	                  <div
	                    className="rounded-2xl p-4"
	                    style={{ background: '#FFF7ED', border: '1px solid rgba(249,115,22,0.28)' }}
                  >
                    <p className="text-sm" style={{ color: '#0B0F0C' }}>
                      Withdraw <span className="font-extrabold text-emerald-700">${Number(wholeWithdrawDisplayBalance).toFixed(2)}</span> from this game back to {lastLoadSource === 'fp' ? 'your FP balance' : 'your wallet'}.
                    </p>
                    {trailingWithdrawDisplayAmount > 0 && (
                      <p className="text-xs mt-2 font-semibold" style={{ color: '#92400E' }}>
                        ${trailingWithdrawDisplayAmount.toFixed(2)} in trailing cents/points stays in the game.
                      </p>
                    )}
		                    <p className="text-xs mt-2" style={{ color: '#9A3412' }}>
		                      Redeem rules are locked to your active load source: <span className="font-extrabold">{withdrawSourceLabel}</span>.
		                    </p>
		                  </div>
                  <WithdrawalLiveGuide
                    source={lastLoadSource}
                    lastLoad={lastLoad}
                    limits={withdrawLimits}
                    currentBalance={wholeWithdrawDisplayBalance}
                  />
                  {withdrawLock.locked && withdrawLock.minAmount > 0 && (
                    <div
                      className="rounded-2xl px-4 py-3 text-xs font-bold leading-relaxed"
                      style={{ background: '#FEF2F2', border: '1px solid rgba(239,68,68,0.30)', color: '#991B1B' }}
                    data-testid={`withdraw-min-lock-${acc.id}`}
                  >
                      {withdrawLock.message} Current redeemable balance is ${Number(wholeWithdrawDisplayBalance).toFixed(2)}.
                    </div>
                  )}
                  <div
                    className="rounded-2xl p-3.5"
                    style={{
                      background: lastLoadSource === 'fp' ? '#FAF5FF' : '#ECFDF5',
                      border: lastLoadSource === 'fp' ? '1px solid rgba(0,107,133,0.28)' : '1px solid rgba(7,95,117,0.28)',
                    }}
                    data-testid={`withdraw-locked-source-${acc.id}`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.16em] font-extrabold" style={{ color: '#64748B' }}>
                      Locked by active load
                    </p>
                    <p className="display text-lg font-extrabold mt-1" style={{ color: '#0B0F0C' }}>
                      {withdrawSourceLabel}
                    </p>
                    <p className="text-xs mt-1 font-semibold" style={{ color: lastLoadSource === 'fp' ? '#004D61' : '#004D61' }}>
                      {lastLoad > 0
                        ? `Active load total: $${lastLoad.toFixed(2)}`
                        : 'No active load total found yet. Main Balance rules will apply by default.'}
                    </p>
                  </div>
			                  <RulesCard
				                    mode={lastLoadSource}
		                    maxMul={activeMaxMul}
		                    lastLoad={lastLoad}
	                    limits={withdrawLimits}
	                    onViewRules={() => openRulesForSource(lastLoadSource)}
	                  />
                  <Button
                    onClick={submitWithdraw}
                    disabled={transactionBusy || withdrawLock.locked || wholeWithdrawDisplayBalance <= 0}
                    className="w-full h-12 display font-extrabold rounded-xl"
                    style={{
                      background: pendingGameOperation || withdrawLock.locked
                        ? 'linear-gradient(180deg, #CBD5E1, #94A3B8)'
                        : 'linear-gradient(180deg, var(--vl-btn-withdraw-from, #FF8A3D), var(--vl-btn-withdraw-to, #E63946))',
                      color: pendingGameOperation || withdrawLock.locked ? '#334155' : 'var(--vl-btn-withdraw-text, #fff)',
                    }}
                    data-testid={`confirm-withdraw-${acc.id}`}
                  >
                    {busy ? <div className="spinner w-4 h-4" /> : (pendingGameOperation ? 'Processing' : (wholeWithdrawDisplayBalance <= 0 ? 'No Whole Balance' : (withdrawLock.locked ? 'Locked Until Min Hit' : 'Confirm Withdraw')))}
                  </Button>
                </>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={replaceOpen} onOpenChange={setReplaceOpen}>
        <SheetContent
          side="bottom"
          overlayStyle={{ zIndex: 70 }}
          style={{ zIndex: 75, paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
          className="player-app rounded-t-[28px] bg-white text-black p-5 max-h-[85dvh] overflow-y-auto"
          data-testid={`replace-account-sheet-${acc.id}`}
        >
          <SheetHeader className="pr-12">
            <SheetTitle>Replace {game.name} account?</SheetTitle>
          </SheetHeader>
          <p className="mt-3 text-sm text-red-700">
            The old account must have a verified zero balance and no pending load or withdrawal.
            Its history is kept, but this card will switch to a new game ID and password.
            Credits are not transferred. The old provider login is not deleted.
            After a successful replacement, this game account cannot be replaced again for 7 days.
          </p>
          <p className="mt-3 text-sm">Type CONFIRM to continue.</p>
          <Input aria-label="Confirm account replacement" value={replaceConfirmation} onChange={(e) => setReplaceConfirmation(e.target.value)} disabled={busy} className="mt-2" />
          {pendingReplacement && <p role="status" className="mt-3 text-sm text-amber-700">Provider outcome is being verified. Contact support; do not replace again.</p>}
          <div className="mt-4 flex gap-3">
            <Button variant="outline" onClick={() => setReplaceOpen(false)}>Close</Button>
            <Button onClick={replaceAccount} disabled={busy || replacementCoolingDown || replaceConfirmation !== 'CONFIRM'} className="bg-red-700 text-white">{busy ? 'Please wait' : 'Replace Account'}</Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <SheetContent
          side="bottom"
          className="player-app p-0 rounded-t-[28px] border-x-0 border-b-0 h-[42dvh] max-h-[420px] overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
            borderColor: 'rgba(212,175,55,0.35)',
            color: '#0B0F0C',
          }}
        >
          <div className="flex h-full flex-col">
            <SheetHeader className="px-5 pt-5 pb-3 pr-16 text-left border-b border-black/5">
	              <p className="text-[10px] uppercase tracking-[0.18em] font-extrabold" style={{ color: '#B45309' }}>
	                Support Required
	              </p>
	              <SheetTitle className="display text-2xl font-extrabold leading-tight" style={{ color: '#0B0F0C' }}>
	                Contact support to reset your pass
	              </SheetTitle>
	              <p className="text-sm font-semibold leading-relaxed" style={{ color: '#64748B' }}>
	                For your safety, game password resets are handled by our agents. Tell support which game you need help with.
	              </p>
            </SheetHeader>

            <div className="flex-1 px-5 py-5">
              <div
                className="rounded-2xl p-4"
                style={{ background: '#FFFBEB', border: '1px solid rgba(245,158,11,0.32)' }}
              >
	                <p className="text-xs font-bold leading-relaxed" style={{ color: '#92400E' }}>
	                  Game: {game.name}. Once support resets it, this card will show the updated password automatically after your lobby syncs.
	                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 px-5 pb-5">
              <Button
                type="button"
                onClick={() => setResetConfirmOpen(false)}
                disabled={busy}
                className="h-12 rounded-xl display font-extrabold"
                style={{ background: '#E5E7EB', color: '#0B0F0C' }}
              >
                Cancel
              </Button>
	              <Button
	                type="button"
	                onClick={openSupportForPasswordReset}
	                className="h-12 rounded-xl display font-extrabold"
                style={{
                  background: 'linear-gradient(180deg, #FACC15, #D4AF37)',
                  color: '#0B0F0C',
                  boxShadow: '0 10px 22px rgba(212,175,55,0.28)',
                }}
	                data-testid={`confirm-reset-game-pw-${acc.id}`}
	              >
	                Contact Support
	              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {showCashoutRules && (
        <CashoutRulesPopup
          gameId={game.id}
          source={rulesPopupSource}
          onClose={() => setShowCashoutRules(false)}
        />
      )}

      {/* Iter 145+: pro-grade no-balance modal — replaces toast.error
          with a polished animated dialog and a Deposit Now CTA that
          opens the wallet deposit flow via global `vl:open-deposit`. */}
      <NoGameBalanceModal
        variant={noBalModal === 'redeem' ? 'redeem' : 'load'}
        open={!!noBalModal}
        onClose={closeNoBalanceModal}
        onDepositNow={goToDepositFromNoBalance}
      />
    </div>
  );
}
